import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Org-scoped JWT bootstrap:
 * 1) If token exists in localStorage, use it and decode tenant/role.
 * 2) Else call GET /auth/issue-jwt with the Clerk session to mint one.
 *    - 404 => user has no org; token stays empty.
 */
export default function useOrgJwt() {
  const { getToken } = useAuth();

  const [orgJwt, setOrgJwt]   = useState(localStorage.getItem('org_jwt') || '');
  const [tenantId, setTid]    = useState('');
  const [role, setRole]       = useState('read-only');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;

    const setFromToken = (token) => {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload?.tenant_id) setTid(payload.tenant_id);
        if (payload?.role && (payload.role === 'admin' || payload.role === 'viewer')) {
          setRole(payload.role);
        }
      } catch {}
    };

    const run = async () => {
      if (orgJwt) {
        setFromToken(orgJwt);
        setLoading(false);
        return;
      }

      try {
        const clerkToken = await getToken();
        if (!clerkToken) {
          setLoading(false);
          return;
        }

        const res = await fetch(`${API_BASE}/auth/issue-jwt`, {
          headers: { Authorization: `Bearer ${clerkToken}` }
        });

        if (res.status === 404) {
          setLoading(false);
          return;
        }
        if (!res.ok) {
          setLoading(false);
          return;
        }

        const data = await res.json();
        if (cancel) return;

        if (data?.token) {
          localStorage.setItem('org_jwt', data.token);
          setOrgJwt(data.token);
          setFromToken(data.token);
          setRole((data.role === 'admin' || data.role === 'viewer') ? data.role : 'read-only');
        }
      } catch {
      } finally {
        if (!cancel) setLoading(false);
      }
    };

    run();
    return () => { cancel = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken]);

  return { orgJwt, tenantId, role, loading };
}
